import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { isBoolean, isNumber } from "@ciaopeople/cp-shatsu/TypeChecking";
import CancelablePromise from "cancelable-promise";
import Semaphore from "semaphore-promise";
import { acquireCancelableSemaphore } from "../../lib/utils/CancelableSemaphoreAcquire.js";
import { CursoredPlaylist } from "../playlist/CursoredPlaylist.js";
import { MediaIdOrPlaylistIndex, PlayerControls, PlayerEventAction, PlayerEventListener, VideoPlayerElements, VideoContext, MediaContext, MediaPlayerElements } from "./Player.js";

import { SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { Action } from "@ciaopeople/cp-shatsu/EventHandler/event-handler";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { ReplaySubject } from "@ciaopeople/cp-shatsu/Observable";
import { MediaCompleteEvent, MediaControlOrigin, MediaEventType, MediaImpressionEvent, MediaLoadStartedEvent, MediaLoadedEvent, MediaPausedEvent, MediaPlaylistStartedEvent, MediaProgressEvent, MediaResumedEvent, MediaStartedEvent, PlayerEvent, isPlayerEvent } from "../events/PlayerEvent.js";
import { MediaEventsConfig, PartialMediaEventsConfig, PlayerControlsConfig } from "./PlayerControlsConfig.js";
import { PlayMode } from "./MediaPlayOptions.js";
import { isMediaId, MediaId, MediaInfoRawData } from "../../model/MediaInfo.js";
import { MediaRef } from "./MediaResource.js";
import { PlayerTechFactory } from "./PlayerTechFactory.js";
import { UserGestureUnmuter } from "../unmuter/UserGestureUnmuter.js";


/**
 * The name of the custom event dispatched by the player controls.
 * Used to notify listeners about player-related actions or state changes.
 */
const PLAYER_EVENT_NAME = "player_event";

/**
 * Represents the event data associated with a media resource in the player.
 *
 * @property startTriggered - Indicates whether the start event has been triggered.
 * @property impressionTriggered - Indicates whether the impression event has been triggered.
 * @property lastCurrentTime - The last known current time of the media resource, in seconds.
 * @property completeTriggered - Indicates whether the complete event has been triggered.
 * @property isPaused - Indicates whether the media resource is currently paused.
 */
type ResourceEventData = {
    startTriggered: boolean,
    impressionTriggered: boolean;
    lastCurrentTime: number;
    completeTriggered: boolean;
    isPaused: boolean
}

/**
 * Represents the possible statuses of media playback.
 *
 * @enum {string}
 * @property {string} LOADING - The media is currently loading.
 * @property {string} PLAYING - The media is actively playing.
 * @property {string} PAUSED - The media playback is paused.
 * @property {string} COMPLETED - The media playback has finished.
 */
export enum MediaStatus {
    LOADING = "loading",
    PLAYING = "playing",
    PAUSED = "paused",
    COMPLETED = "completed"
}

/**
 * Represents the current state of the media, including its status and the origin of the control action.
 *
 * @property status - The current status of the media (e.g., playing, paused, stopped).
 * @property origin - The source of the media control action (e.g., user, system).
 */
export interface MediaState {
    status: MediaStatus,
    origin: MediaControlOrigin
}

/**
 * Represents a subscription to media status updates.
 * Provides methods to unsubscribe from the updates and to check if the subscription is closed.
 *
 * @remarks
 * Implementations should ensure that `unsubscribe` properly cleans up resources,
 * and `isClosed` accurately reflects the subscription state.
 *
 * @method unsubscribe Unsubscribes from media status updates.
 * @method isClosed Returns `true` if the subscription has been closed, otherwise `false`.
 */
export interface MediaStatusSubscription {
    unsubscribe(): void;
    isClosed(): boolean;
}


/**
 * The default resolution, in seconds, for emitting progress events from the player.
 * This value determines how frequently progress updates are dispatched.
 */
const DEF_PROGRESS_EVT_RESOLUTION_SEC = 1;
/**
 * Indicates whether dynamic viewable impression tracking is enabled.
 * When set to `true`, the player will use dynamic logic to determine viewable impressions.
 */
const DEF_DYNAMIC_VIEWABLE_IMPRESSION = true;
/**
 * The default duration in seconds for the dynamic viewable impression cap.
 * Used to limit the maximum time a dynamic viewable impression can be counted.
 */
const DEF_DYNAMIC_VIEWABLE_IMPRESSION_CAP_SEC = 5;


/**
 * Builds a complete `MediaEventsConfig` object from a partial configuration.
 * 
 * For each property in the partial config, if the value is valid (correct type), it is used;
 * otherwise, a default value is assigned.
 *
 * @param part - A partial configuration object for media events.
 * @returns A fully populated `MediaEventsConfig` object with defaults for missing or invalid properties.
 */
function buildEventParamConfig(part: PartialMediaEventsConfig): MediaEventsConfig {
    return {
        enable: part.enable,
        progress_resolution: isNumber(part.progress_resolution) ? part.progress_resolution : DEF_PROGRESS_EVT_RESOLUTION_SEC,
        dynamic_viewable_impression: isBoolean(part.dynamic_viewable_impression) ? part.dynamic_viewable_impression : DEF_DYNAMIC_VIEWABLE_IMPRESSION,
        dynamic_viewable_impression_cap: isNumber(part.dynamic_viewable_impression_cap) ? part.dynamic_viewable_impression_cap : DEF_DYNAMIC_VIEWABLE_IMPRESSION_CAP_SEC
    }
}


/**
 * Represents a partial set of event data related to a video.
 *
 * @property mediaId - The unique identifier for the media item.
 * @property playlistId - The identifier for the playlist containing the media.
 * @property info - Raw information about the media, with a generic type.
 * @property [k: string] - Additional arbitrary properties.
 */
type PartialVideoEventData = {
    mediaId: MediaId,
    playlistId: string,
    info: MediaInfoRawData<any>,
    [k: string]: any
}


/**
 * Represents a collection of event listeners for media resource playback events.
 *
 * @property progress - Listener for the progress event, typically fired as the media resource loads.
 * @property playing - Listener for the playing event, triggered when playback starts or resumes.
 * @property pause - Listener for the pause event, triggered when playback is paused.
 * @property ended - Listener for the ended event, triggered when playback has completed.
 */
type ResourceListeners = {

    progress: EventListenerOrEventListenerObject,
    playing: EventListenerOrEventListenerObject,
    pause: EventListenerOrEventListenerObject,
    ended: EventListenerOrEventListenerObject

}



export const QUALITY_PREFERENCE_KEY = "qual_pref"

/**
 * Abstract base class for implementing player controls for media players.
 * 
 * This class provides a generic, extensible framework for managing media playback, playlist navigation,
 * event handling, and resource management. It is designed to be subclassed for specific player implementations.
 * 
 * @typeParam EL - The type of the HTML media element (e.g., HTMLVideoElement, HTMLAudioElement).
 * @typeParam MI - The type representing raw media information.
 * @typeParam REF - The type representing a reference to a media resource.
 * @typeParam CTX - The type representing the media context, which manages playback and state.
 * @typeParam PlaylistType - The type representing a playlist with cursor navigation.
 * 
 * @implements PlayerControls
 * 
 * @remarks
 * - Subclasses must implement methods for fetching and clearing media elements, starting playback, handling errors,
 *   and performing resource-specific operations.
 * - Provides event subscription and triggering mechanisms for player events.
 * - Manages mute state, resource listeners, and media state transitions.
 * - Handles preloading, unloading, and building media contexts for playback.
 * 
 * @example
 * ```typescript
 * class MyPlayerControls extends AbstractPlayerControls<HTMLVideoElement, MyMediaInfo, MyMediaRef, MyMediaContext, MyPlaylist> {
 *   // Implement abstract methods...
 * }
 * ```
 */
export abstract class AbstractPlayerControls<
    EL extends HTMLMediaElement,
    MI extends MediaInfoRawData<any>,
    REF extends MediaRef<EL, MI>,
    CTX extends MediaContext<REF>,
    PlaylistType extends CursoredPlaylist<MI>> implements PlayerControls<EL, MI, REF, CTX> {

    private playingMediaContext: CTX;

    protected readonly vwDetect: DetectedWebView;
    protected logger: Logger;

    private generalMute: boolean;
    private playMonitor: Semaphore;

    private events: SimpleEventHandler;

    private eventDataMem: Record<MediaId, ResourceEventData>;
    private resourceListeners: ResourceListeners;
    private playlistStartedPushed: boolean;

    private eventParams: MediaEventsConfig;

    private currentMediaStateSub: ReplaySubject<MediaState> = new ReplaySubject(1, { status: MediaStatus.LOADING, origin: MediaControlOrigin.NON_USER })

    protected readonly factory: PlayerTechFactory<any, EL, MI, REF, CTX, any>


    constructor(
        protected readonly playerName: string,
        protected playlist: PlaylistType,
        public config: PlayerControlsConfig,
        factoryBuilder: (controls: PlayerControls<EL, MI, REF, CTX>) => PlayerTechFactory<any, EL, MI, REF, CTX, any>,
        protected readonly win: Window,
        protected unmuter: UserGestureUnmuter
    ) {
        this.logger = new BrowserLogger(`[${playerName}]`, "#8403fc")
        this.logger.debug("Running playlist: ", this.playlist.name);
        this.vwDetect = detectWebView();
        this.generalMute = true;
        this.playMonitor = new Semaphore(1);
        this.events = new SimpleEventHandler();
        this.eventDataMem = {};

        this.eventParams = buildEventParamConfig(config.events);
        this.playlistStartedPushed = false;
        this.resourceListeners = { progress: null, playing: null, pause: null, ended: null };

        this.factory = factoryBuilder(this)
    }

    get currentMediaState(): MediaState {
        return this.currentMediaStateSub.getCurrentValue()
    }

    subscribeMediaStatus(fn: (s: MediaState) => void): MediaStatusSubscription {
        const unsub = this.currentMediaStateSub.subscribe(fn);
        return {
            unsubscribe: () => {
                unsub.unsubscribe()
            },
            isClosed: () => {
                return unsub.isClosed();
            }
        }
    }


    getCurrentPlaylistId(): string {
        return this.playlist.name;
    }
    getCurrentPlayingMediaContext(): CTX {
        return this.playingMediaContext;
    }

    protected unloadCurrentMediaContext(): Promise<CTX | null> {
        let unloadedContextPromise = Promise.resolve(null);
        if (this.playingMediaContext) {
            this.detachListenersToContext(this.playingMediaContext);
            return this.playingMediaContext.beforeUnload().then(_done => {
                this.playingMediaContext.pause(MediaControlOrigin.USER);
                this.playingMediaContext.autoplay(false);
                this.factory.unloadContext(this.playingMediaContext.ref.info.id)
                const prevElement = this.playingMediaContext;
                this.playingMediaContext = null;
                return prevElement;
            })
        }
        return unloadedContextPromise;
    }



    /**
     * this method is called during media load, and should return the player media element
     * to use in order to produce a new media context;
     * this method is left abstract to let the programmer choose, depending on player gui implementation,
     * how to fetch or eventually generate a new media element
     * @param mediaInfo 
     */
    public abstract fetchPlayerMediaElements(mediaID: MediaId): MediaPlayerElements<EL>;

    /**
     * this method is called during resource cleanup and can be use to free resources (dom, gui, whatever) attached to 
     * a specific media 
     * @param mediaInfo 
     */
    public abstract clearPlayerMediaElement(mediaID: MediaId): void;


    protected preloadMediaContext(mediaInfo: MI): CancelablePromise<CTX> {

        const mpe: MediaPlayerElements<EL> = this.fetchPlayerMediaElements(mediaInfo.id);

        const beforePreload = (mediaInfo: MI) => {
            this.logger.debug("Preloading media context", mediaInfo)
            const toTrigger: MediaLoadStartedEvent = {
                executionId: "no-available-id",
                eventType: MediaEventType.MEDIA_LOAD_STARTED,
                mediaId: mediaInfo.id,
                playlistId: this.getCurrentPlaylistId(),
                timing: {
                    timeOrigin: this.win.performance.now()
                },
                info: mediaInfo
            }
            this.triggerEvent(toTrigger)

            return mediaInfo
        }

        return this.factory.getOrPreloadMediaContext(mediaInfo, mpe, this.isForwardNavigation(mediaInfo.id), beforePreload)
            .then(ctx => {
                ctx.metadataPromise.then(_ => {
                    this.triggerMediaLoadedEvent(ctx, false);
                });

                if (this.factory.config.playOptions.timeCutSeconds > 0) {
                    ctx.on("timeupdate", (evt) => {
                        const cTime = Math.floor(ctx.currentTime);
                        if (cTime >= this.factory.config.playOptions.timeCutSeconds) {
                            this.actionOnMediaComplete(ctx, true);
                        }
                    });

                }
                return ctx;
            });

    }

    /**
     * it is called by startVideo method just before playing selected resource
     * this method could be used to perform implementation specific operations 
     * on the resource
     * @param ctx 
     */
    protected abstract beforeResourceStart(ctx: CTX): void;



    /**
     * preloads and inits selected video
     * before starting and releasing the related media resource preloads are managed
     * according to defined strategy
     * 
     * @param mediaInfo 
     * @returns 
     */
    protected buildMediaContext(mediaInfo: MI): CancelablePromise<CTX> {
        return acquireCancelableSemaphore(this.playMonitor).then((release) => {

            this.logger.debug("Loading video", mediaInfo);

            const promise = this.preloadMediaContext(mediaInfo);

            return this.unloadCurrentMediaContext().then(unloaded => {
                this.logger.debug("Unloaded Current video", unloaded?.ref.info?.id);

                return this.factory.managePreloads(mediaInfo)
                    .then(_ => promise.then(context => {
                        this.logger.debug("Got resource to play", mediaInfo);
                        this.beforeResourceStart(context);
                        context.show();
                        this.attachListenersToResource(context);

                        this.playingMediaContext = context;
                        release();
                        return context;
                    }));
            })

        });

    }


    public abstract play(mediaIdOrIndex: MediaIdOrPlaylistIndex, forceResume: boolean): Promise<CTX>

    onPlayerEvent(listener: PlayerEventListener): PlayerEventAction {
        const rListener: Action = (name: string, evt: any) => {
            try {
                if (isPlayerEvent(evt)) listener(evt);
            } catch (e) {
                this.logger.error(e);
            }
        }
        this.events.on(PLAYER_EVENT_NAME, rListener);
        return rListener;
    }

    offPlayerEvent(listener: PlayerEventAction): void {
        this.events.off(PLAYER_EVENT_NAME, listener);
    }


    protected triggerEvent(evt: PlayerEvent) {
        if (this.eventParams.enable) {
            this.events.trigger(PLAYER_EVENT_NAME, evt);
        }
        else console.log("Player Events Disabled - not triggering event:", evt);
    }



    private isForwardNavigation(nextMediaID: MediaId): boolean {
        let forwardNavigation = true;
        const currentMediaCtx = this.playingMediaContext;
        if (currentMediaCtx) {
            const currentIndex = this.playlist.getPosById(currentMediaCtx.ref.info.id);
            const nextIndex = this.playlist.getPosById(nextMediaID);
            forwardNavigation = nextIndex > currentIndex;
        }
        return forwardNavigation
    }

    protected abstract wrapMediaPlayerContext(prevCtx: CTX): CTX

    protected triggerMediaLoadedEvent(mediaCtx: CTX, forcedTrigger: boolean): void {
        const basicEvent = this.getBasicEventData(mediaCtx.ref.info);
        const mle: MediaLoadedEvent = {
            ...basicEvent,
            executionId: mediaCtx.ref.executionId,
            eventType: MediaEventType.MEDIA_LOADED,
            metadata: {
                duration: mediaCtx.duration
            },
            genuine: !forcedTrigger
        }
        this.triggerEvent(mle);

    }



    protected extractMediaFromPlaylist(idOrIndex: MediaIdOrPlaylistIndex): Promise<MI> {
        let getPromise: Promise<MI> = Promise.resolve(null);
        if (isNumber(idOrIndex)) {
            getPromise = this.playlist.goto(idOrIndex);
        }
        else if (isMediaId(idOrIndex)) {
            getPromise = this.playlist.seek(idOrIndex);
        }

        return getPromise.then(info => {
            return info ? Promise.resolve(info) : Promise.reject("Invalid position specified");
        })
    }



    public toggleGeneralMute() {

        if (this.playingMediaContext) {
            return this.playingMediaContext.muted()
                .then(muted => {
                    //#13804
                    // this.logger.info("AGGRO called toggleGeneralMute ismuted", muted, " unmuter status", this.unmuter.currentUnmuteStatus())
                    const unmutePromise = !muted ? this.unmuter.afterUnmute() : Promise.resolve();

                    return unmutePromise.then(_ => {
                        this.generalMute = !muted;
                        return this.generalMute
                    })

                });
        }

        return Promise.resolve(this.generalMute)

    }

    public getGeneralMute(): boolean {
        return this.generalMute;

    }

    protected setGeneralMute(enable: boolean) {
        // this.generalMute = enable;
        if (this.playingMediaContext) {
            return this.playingMediaContext.muted(enable)
                .then(e => { return })
        }

        return Promise.resolve()
    }

    public abstract signalError(s: string): void;

    private getStatisticalDataForRes(mediaId: MediaId): ResourceEventData {
        return this.eventDataMem[mediaId];
    }
    private getBasicEventData(media: MI): PartialVideoEventData {
        return {
            mediaId: media.id,
            playlistId: this.getCurrentPlaylistId(),
            info: media
        };
    }


    protected internalProgressConverter(evt: Event, ctx: CTX): void {
        const mediaId = ctx.ref.info.id;
        const datamem = this.getStatisticalDataForRes(mediaId);
        const basicEvent = this.getBasicEventData(ctx.ref.info);


        const currentTimeInSec = Math.floor(ctx.currentTime);
        const diff = currentTimeInSec - datamem.lastCurrentTime;
        const durationInSec = Math.floor(ctx.duration);
        const sensitivity = 0.2;
        const progV = Math.trunc(currentTimeInSec / durationInSec * 100);


        let impressionCondition = DEF_DYNAMIC_VIEWABLE_IMPRESSION ?
            currentTimeInSec >= DEF_DYNAMIC_VIEWABLE_IMPRESSION_CAP_SEC || progV >= 10 :
            currentTimeInSec <= (2 * DEF_PROGRESS_EVT_RESOLUTION_SEC);

        //console.log("EVT-progress","trimmedTime","duration","diff",currentTimeInSec,durationInSec,diff);

        if (!datamem.startTriggered && ctx.currentTime >= 0.1) {
            datamem.startTriggered = true;
            const timed = this.win.performance.now();
            const toTrigger: MediaStartedEvent = {
                ...basicEvent,
                executionId: ctx.ref.executionId,
                eventType: MediaEventType.MEDIA_STARTED,
                timing: {
                    timeOrigin: timed,
                    duration: timed - ctx.stats.loadStartTime
                },
            }
            this.triggerEvent(toTrigger);
        }


        if (impressionCondition && !datamem.impressionTriggered) {
            //console.log("EVT-progress","going impression","videoid", basicEvent.videoId, "time",currentTimeInSec);
            const toTrigger: MediaImpressionEvent = {
                ...basicEvent,
                executionId: ctx.ref.executionId,
                eventType: MediaEventType.MEDIA_IMPRESSION
            }
            this.triggerEvent(toTrigger);
            datamem.impressionTriggered = true;
        }
        /** temporarily commented event to be checked on flash */
        // else if (Math.abs(res.duration - res.currentTime) <= sensitivity) {
        //     // console.log("EVT-progress", "complete", "videoid", basicEvent.videoId, currentTimeInSec, "timediff",
        //     //     Math.abs(res.duration - res.currentTime), "sensitivity", sensitivity);
        //     this.actionOnVideoComplete(res, false);
        // }

        //progress
        if (Math.abs(diff) >= DEF_PROGRESS_EVT_RESOLUTION_SEC) {
            //?TODO: Verificare che questa operazione non abbia distrutto le sstatistiche flash
            // if (diff < 0) {
            //     datamem.completeTriggered = false;
            //     datamem.impressionTriggered = false;
            // }
            //?ENDOF

            const progressEvent: MediaProgressEvent = {
                ...basicEvent,
                executionId: ctx.ref.executionId,
                eventType: MediaEventType.MEDIA_PROGRESS,
                progress: progV,
                currentTime: currentTimeInSec
            };
            this.triggerEvent(progressEvent);
            datamem.lastCurrentTime = currentTimeInSec;
        }

    }


    private toggleResourcePlayStatus(ctx: CTX, newPlayStatus: boolean): void {
        const datamem = this.getStatisticalDataForRes(ctx.ref.info.id);
        const newPausedStatus = !newPlayStatus;
        const origin = ctx.fetchLastMediaControlOrigin();



        if (datamem.isPaused != newPausedStatus) {
            datamem.isPaused = newPausedStatus;
            this.currentMediaStateSub.next({ status: datamem.isPaused ? MediaStatus.PAUSED : MediaStatus.PLAYING, origin: origin })
            const basicEvent = this.getBasicEventData(ctx.ref.info);
            const toTrigger: MediaPausedEvent | MediaResumedEvent = {
                ...basicEvent,
                executionId: ctx.ref.executionId,
                eventType: newPausedStatus ? MediaEventType.MEDIA_PAUSED : MediaEventType.MEDIA_RESUMED,
                currentTime: ctx.currentTime,
                origin: origin
            }
            this.triggerEvent(toTrigger);
        }


    }

    private attachListenersToResource(ctx: CTX): void {
        if (!(ctx.ref.executionId in this.eventDataMem)) this.eventDataMem[ctx.ref.info.id] = {
            impressionTriggered: false,
            lastCurrentTime: 0,
            completeTriggered: false,
            startTriggered: false,
            isPaused: true
        }
        if (!this.playlistStartedPushed) {
            const toTrigger: MediaPlaylistStartedEvent = {
                executionId: ctx.ref.executionId,
                playlistId: this.playlist.name,
                mediaId: ctx.ref.info.id,
                eventType: MediaEventType.PLAYLIST_STARTED,
                info: ctx.ref.info
            }
            this.triggerEvent(toTrigger);
            this.playlistStartedPushed = true;
        }
        this.currentMediaStateSub.next({ status: MediaStatus.LOADING, origin: MediaControlOrigin.NON_USER });

        this.resourceListeners.progress = (evt: Event) => {
            this.internalProgressConverter(evt, ctx);
        }
        this.resourceListeners.pause = (evt: Event) => {
            this.toggleResourcePlayStatus(ctx, false);
        }
        this.resourceListeners.playing = (evt: Event) => {
            this.toggleResourcePlayStatus(ctx, true);
        }
        this.resourceListeners.ended = (evt: Event) => {
            this.actionOnMediaComplete(ctx, this.factory.config.playOptions.playMode == PlayMode.LOOP);
        }


        ctx.on("timeupdate", this.resourceListeners.progress);
        ctx.on("ended", this.resourceListeners.ended)
        ctx.on("pause", this.resourceListeners.pause);
        ctx.on("playing", this.resourceListeners.playing);

    }

    private detachListenersToContext(res: CTX): void {
        if (this.resourceListeners?.progress) res.off("timeupdate", this.resourceListeners.progress);
        if (this.resourceListeners?.playing) res.off("playing", this.resourceListeners.playing);
        if (this.resourceListeners?.pause) res.off("pause", this.resourceListeners.pause);
        if (this.resourceListeners?.ended) res.off("ended", this.resourceListeners.ended);
    }


    private actionOnMediaComplete(ctx: CTX, loopMode: boolean): void {
        const mediaId = ctx.ref.info.id;
        const datamem = this.getStatisticalDataForRes(mediaId);
        if (!datamem.completeTriggered) {
            const basicEvent = this.getBasicEventData(ctx.ref.info);
            const toTrigger: MediaCompleteEvent = {
                ...basicEvent,
                executionId: ctx.ref.executionId,
                eventType: MediaEventType.MEDIA_COMPLETE
            }
            this.triggerEvent(toTrigger);
            datamem.completeTriggered = true;

        }

        this.currentMediaStateSub.next({ status: MediaStatus.COMPLETED, origin: MediaControlOrigin.NON_USER });

        if (loopMode) {
            ctx.restart();
            datamem.completeTriggered = false;
            datamem.impressionTriggered = false;
        }

    }

    protected beforeResourceRestart(videoId: MediaId): void {
        const datamem = this.getStatisticalDataForRes(videoId);
        datamem.startTriggered = false;
        datamem.completeTriggered = false;
        datamem.impressionTriggered = false;
    }

}



